<template>
  <div
    ref="root"
    class="c-base-button-dropdown"
  >
    <slot
      :attrs="triggerAttributes"
      :listeners="buttonListeners"
      name="btn"
      :toggle="toggle"
      :visible="visible"
    >
      <BaseButton
        ref="triggerButton"
        v-bind="triggerAttributes"
        :active="active"
        :active-color="activeColor"
        :class="twMerge(menuItem && 'tw:w-full', buttonClass)"
        :data-testid="openDropdownButtonTestid"
        :disabled="disabled"
        :icon="icon"
        :icon-size="iconSize"
        :label="label"
        :label-class="labelClass"
        :menu-item="menuItem"
        :new-design="newDesign"
        :new-icon="newIcon"
        v-on="buttonListeners"
      >
        <template #after>
          <BaseIcon
            v-if="chevron"
            class="chevron"
            :class="{
              rotate: visible,
              'tw:text-xs': label,
              'tw:text-lg': !label,
            }"
            icon="angle-down"
          />
          <slot name="tooltip" />
        </template>
      </BaseButton>
    </slot>

    <Teleport
      :disabled="!teleport"
      :to="teleport"
    >
      <Popper
        :id="dropdownId"
        :key="uid"
        ref="popper"
        v-model="visible"
        :boundary="boundary"
        :caret="caret"
        class="c-base-button-dropdown__body"
        :class="
          twMerge(
            newDropdown ? 'c-dropdown-menu' : 'menu-shadow-box rounded-borders',
            dropdownClass
          )
        "
        :data-dropdown-id="uid"
        disable-click-outside
        :fallback-placements="fallbackPlacements"
        :flip="flip"
        :offset="dropdownOffset"
        :placement="placement"
        :root-element="root"
        :same-width="sameWidthPopper"
        :tether="tether"
        v-on="bodyListeners"
      >
        <slot />
      </Popper>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { useFocusTrap } from "@vueuse/integrations/useFocusTrap";
import { omit } from "lodash-es";
import { twMerge } from "tailwind-merge";
import {
  type ComponentPublicInstance,
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  type TeleportProps,
  useAttrs,
  useId,
  watch,
} from "vue";

import { BaseButton, BaseIcon } from "shared/components/base";
import Popper, { type PopperProps } from "shared/components/base/BasePopper";
import type { Maybe, Nullable } from "shared/types";

type Interaction = "click" | "hover";

type DocumentEventName = Pick<
  HTMLBodyElementEventMap,
  "click" | "mouseleave" | "mouseenter"
>;

type DocumentEventListeners = {
  [K in keyof DocumentEventName]?: (event: MouseEvent) => void;
};

interface Dropdown {
  $el: HTMLDivElement;
  hide: () => boolean;
}

export interface Props extends Pick<
  PopperProps,
  "boundary" | "fallbackPlacements" | "flip" | "offset" | "placement" | "tether"
> {
  active?: boolean;
  activeColor?: string;
  buttonClass?: string;
  caret?: boolean;
  chevron?: boolean;
  closeOnClick?: boolean;
  disableClickOutside?: boolean;
  disabled?: boolean;
  dropdownClass?: string;
  icon?: string;
  iconSize?: string;
  interaction?: Interaction;
  label?: string;
  labelClass?: string;
  menuItem?: boolean;
  modelValue?: boolean;
  newDesign?: boolean;
  newDropdown?: boolean;
  newIcon?: boolean;
  openDropdownButtonTestid?: string;
  sameWidthPopper?: boolean;
  submenu?: boolean;
  teleport?: TeleportProps["to"];
  trapFocus?: boolean;
}

const visible = defineModel<boolean>();

const props = withDefaults(defineProps<Props>(), {
  activeColor: "activated",
  boundary: null,
  buttonClass: "",
  dropdownClass: "",
  openDropdownButtonTestid: undefined,
  fallbackPlacements: () => ["top"],
  flip: true,
  icon: "",
  iconSize: "",
  interaction: "click",
  label: "",
  labelClass: "",
  offset: undefined,
  placement: "bottom",
  teleport: null,
});

const emit = defineEmits<{
  click: [boolean];
  "click-outside": [];
  hide: [];
  show: [];
}>();

const attrs = useAttrs();

const uid = ref(useId());

const timeout = ref<ReturnType<typeof setTimeout>>();

let visibleDropdowns: Dropdown[] = [];
let mousedown = false;
let triggerElement: Nullable<HTMLElement> = null;
const root = ref<HTMLDivElement>();
const triggerButton = ref<ComponentPublicInstance>();
const popper = ref<ComponentPublicInstance>();

const dropdownId = computed<string>(() => `base-button-dropdown-${uid.value}`);

const dropdownOffset = computed<Props["offset"]>(() => {
  if (props.offset) {
    return props.offset;
  }

  return props.newDropdown ? [0, 1] : [0, 0];
});

const documentBody = document.querySelector("body");

function closeDropdowns(event: MouseEvent): void {
  if (mousedown) {
    mousedown = false;

    return;
  }

  if (!event.target) {
    return;
  }

  visibleDropdowns.forEach((dropdown, index) => {
    const dropdownContent = document.querySelector(
      `.c-base-button-dropdown__body[data-dropdown-id="${uid.value}"]`
    );

    if (
      dropdown &&
      !dropdown.$el.contains(event.target as Node) &&
      !dropdownContent?.contains(event.target as Node)
    ) {
      const hidden = dropdown.hide();
      if (!hidden) return;
      visibleDropdowns.splice(index, 1);
      emit("click-outside");
    }
  });
}

function focusTrigger(): void {
  const fallback: Maybe<HTMLElement> = triggerButton.value?.$el;

  (triggerElement ?? fallback)?.focus();

  triggerElement = null;
}

// Teleported, the panel no longer follows the trigger in the tab order, so Tab
// walks the page behind it. Opt-in: menus want Tab to leave, panels want it held.
const dropdownBody = ref<Maybe<HTMLElement>>();

const { deactivate: releaseFocus } = useFocusTrap(dropdownBody, {
  allowOutsideClick: true,
  // The panel is already on screen; the default setTimeout only delays focus.
  delayInitialFocus: false,
  // onDocumentKeydown handles Escape and restores focus.
  escapeDeactivates: false,
  fallbackFocus: () => dropdownBody.value ?? document.body,
  // Armed by handing it an element, so it activates on open.
  immediate: true,
  returnFocusOnDeactivate: false,
});

watch(visible, async (isVisible) => {
  if (!props.trapFocus) {
    return;
  }

  // `close` covers its own paths; this catches a model-driven close.
  if (!isVisible) {
    releaseFocus();
    dropdownBody.value = undefined;

    return;
  }

  // The panel is behind a v-if, so it only exists once the open has rendered.
  await nextTick();

  const panel = popper.value?.$el;

  dropdownBody.value = panel instanceof HTMLElement ? panel : undefined;
});

function onDocumentKeydown(event: KeyboardEvent): void {
  if (event.key !== "Escape" || !visible.value) {
    return;
  }

  // Escape is an explicit close request, so it bypasses the
  // disableClickOutside guard that only applies to click-away dismissal
  close();
  focusTrigger();
}

function addDocumentListener(): void {
  documentBody?.addEventListener("click", closeDropdowns, true);
  document.addEventListener("keydown", onDocumentKeydown);
}

function removeDocumentListener(): void {
  documentBody?.removeEventListener("click", closeDropdowns, true);
  document.removeEventListener("keydown", onDocumentKeydown);
}

function close(): void {
  // Before anything moves focus: an armed trap pulls it back into the
  // closing panel, stranding it on the body.
  releaseFocus();
  dropdownBody.value = undefined;

  visible.value = false;
  removeDocumentListener();

  visibleDropdowns = visibleDropdowns.filter(
    (dropdown) => dropdown.$el !== root.value
  );

  emit("hide");
}

function hide(): boolean {
  if (props.disableClickOutside) {
    return false;
  }

  close();

  return true;
}

function show(): void {
  const active = document.activeElement;

  triggerElement =
    active instanceof HTMLElement && root.value?.contains(active)
      ? active
      : null;

  visible.value = true;
  addDocumentListener();
  visibleDropdowns.push({ $el: root.value!, hide });
  emit("show");
}

function toggle(): void {
  if (visible.value) {
    hide();
  } else {
    show();
  }
}

function handleHideEvent({ relatedTarget }: MouseEvent): void {
  if (!relatedTarget) {
    return;
  }

  const target = relatedTarget as HTMLElement;

  const dropdownContent = document.querySelector(
    `.c-base-button-dropdown__body[data-dropdown-id="${uid.value}"]`
  );

  if (root.value?.contains(target) || dropdownContent?.contains(target)) {
    return;
  }

  hide();
}

function onButtonClick(event: MouseEvent): void {
  event.stopPropagation();

  if (props.interaction === "hover") {
    return;
  }

  toggle();
  emit("click", visible.value as boolean);
}

function onButtonEnter(): void {
  show();
}

function onButtonLeave(event: MouseEvent): void {
  if (props.submenu) {
    timeout.value = setTimeout(hide, 150);
  }

  handleHideEvent(event);
}

function onBodyClick(): void {
  if (props.closeOnClick) {
    hide();
  }
}

function onBodyEnter(): void {
  if (timeout.value) {
    clearTimeout(timeout.value);
  }
}

function onBodyLeave(event: MouseEvent): void {
  handleHideEvent(event);
}

defineExpose({ close });

const triggerAttributes = computed<Record<string, unknown>>(() => ({
  ...omit(attrs, [
    "aria-controls",
    "aria-expanded",
    "aria-haspopup",
    "class",
    "style",
  ]),
  ...(visible.value && { "aria-controls": dropdownId.value }),
  "aria-expanded": String(Boolean(visible.value)),
  "aria-haspopup": attrs["aria-haspopup"] ?? "menu",
}));

const buttonListeners = computed<DocumentEventListeners>(() => ({
  click: onButtonClick,
  ...(props.interaction === "hover" && {
    mouseenter: onButtonEnter,
    mouseleave: onButtonLeave,
  }),
}));

const bodyListeners = computed<DocumentEventListeners>(() => {
  if (props.interaction === "hover") {
    return {
      mouseenter: onBodyEnter,
      mouseleave: onBodyLeave,
    };
  }

  return { click: onBodyClick };
});

watch(
  () => props.modelValue,
  (isVisible, oldIsVisible) => {
    if (isVisible === oldIsVisible || oldIsVisible === undefined) return;

    if (props.modelValue !== isVisible) {
      if (props.modelValue) {
        show();
      } else {
        hide();
      }
    }
  },
  { immediate: true }
);

function onMouseDown(): void {
  mousedown = true;
}

function onMouseUp(): void {
  mousedown = false;
}

onBeforeUnmount(() => {
  removeDocumentListener();
  root.value?.removeEventListener("mousedown", onMouseDown);
  root.value?.removeEventListener("mouseup", onMouseUp);
});

onMounted(() => {
  root.value?.addEventListener("mousedown", onMouseDown);
  root.value?.addEventListener("mouseup", onMouseUp);
});
</script>

<style lang="scss" scoped>
.c-base-button-dropdown {
  &__body {
    z-index: 10000;
    overflow: visible;
    background: var(--s-card-bg);
  }

  .chevron {
    transition: transform 0.1s ease;

    &.rotate {
      transform: rotate(180deg);
    }
  }
}
</style>
